import { useRef, useState } from "react";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Progress } from "../../components/ui/progress";
import { Separator } from "../../components/ui/separator";
import { grayColor } from "Common/commaon";
import { useNavigate } from "react-router-dom";
import apiUrl from "slices/apiEndpoint";
import { getDecodeId, getUserType } from "slices/userProfile/thunk";
import { useSelector } from "react-redux";
import { selectUserDetails } from "slices/userProfile/reducer";

export function KYCForm() {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[]>([]);
  const userType = getUserType();
  const user = useSelector(selectUserDetails);

  const kycSections = [
    {
      id: "personal_info",
      title: "Personal Information",
      description: "Please provide your personal details for Stripe verification.",
      fields: [
        { name: "first_name", label: "First Name", type: "text", required: true, placeholder: "John" },
        { name: "last_name", label: "Last Name", type: "text", required: true, placeholder: "Doe" },
        { name: "email", label: "Email Address", type: "email", required: true, placeholder: "john@example.com" },
        { name: "phone", label: "Phone Number", type: "tel", required: true, placeholder: "+1 (555) 000-0000" },
        { name: "dob", label: "Date of Birth", type: "date", required: true },
        {
          name: "ssn_last_4",
          label: "Last 4 digits of SSN",
          type: "text",
          required: true,
          placeholder: "1234",
          validation: { pattern: /^[0-9]{4}$/, minLength: 4, maxLength: 4 },
        },
      ],
    },
    {
      id: "address",
      title: "Address Details",
      description: "Enter your residential or business address.",
      fields: [
        { name: "line1", label: "Address Line 1", type: "text", required: true, placeholder: "123 Main Street" },
        { name: "line2", label: "Address Line 2", type: "text", required: false, placeholder: "Apartment, Suite, etc." },
        { name: "city", label: "City", type: "text", required: true, placeholder: "Los Angeles" },
        { name: "state", label: "State", type: "text", required: true, placeholder: "California" },
        { name: "postal_code", label: "Postal Code", type: "text", required: true, placeholder: "90001" },
      ],
    },
    {
      id: "bank",
      title: "Bank Information",
      description: "Provide your U.S. bank account details for payouts.",
      fields: [
        { name: "account_number", label: "Account Number", type: "text", required: true, placeholder: "000123456789" },
        { name: "routing_number", label: "Routing Number", type: "text", required: true, placeholder: "110000000" },
      ],
    },
  ];

  const section = kycSections[currentSection];
  const totalSections = kycSections.length;
  const progressPercentage = ((currentSection + 1) / totalSections) * 100;

  const handleChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateSection = () => {
    let valid = true;
    const newErrors: Record<string, string> = {};
    section.fields.forEach((f) => {
      const val = formData[f.name];
      if (f.required && !val) {
        newErrors[f.name] = `${f.label} is required`;
        valid = false;
      }
      if (val && f.validation?.pattern && !f.validation.pattern.test(val)) {
        newErrors[f.name] = `${f.label} is invalid`;
        valid = false;
      }
    });
    setErrors(newErrors);
    return valid;
  };

  const handleNext = async () => {
    if (!validateSection()) return;
    if (currentSection < totalSections - 1) {
      setCurrentSection((prev) => prev + 1);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        user_id: getDecodeId(),
        role: userType,
        email: formData.email || user?.email,
        first_name: formData.first_name || user?.first_name,
        last_name: formData.last_name || user?.last_name,
        dob: formData.dob,
        ssn_last_4: formData.ssn_last_4,
        address: {
          line1: formData.line1,
          line2: formData.line2,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postal_code,
        },
        phone: formData.phone || `+1${user?.mobNumber}`,
        bank_account: {
          account_number: formData.account_number,
          routing_number: formData.routing_number,
        },
        ip: "",
      };

      const res = await fetch(`${apiUrl}/stripe/api/submit-stipe-kyc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Submission failed");

      alert("✅ KYC submitted successfully!");
      navigate("/kyc-status-page");
    } catch (err: any) {
      console.error("❌ KYC Error:", err);
      alert(err.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentSection > 0) setCurrentSection((prev) => prev - 1);
    else navigate("/kyc-status-page");
  };

  return (
    <div className="page-content tailwind-prefix">
      <div className="container max-w-3xl">
        <Button variant="ghost" className="mb-4 border-0" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[14px] font-medium">Complete KYC Verification</div>
            <span className="text-sm text-slate-600">
              Step {currentSection + 1} of {totalSections}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2 bg-gray-200" />
        </div>

        <Alert className="mb-6 border-blue-200 bg-blue-50">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-900">
            All information is encrypted and securely processed.
          </AlertDescription>
        </Alert>

        <Card className="p-6 md:p-8 bg-white">
          <div style={{ borderBottom: `1px solid ${grayColor}` }} className="mb-3 pb-3">
            <div className="mb-2 text-[17px] font-semibold">{section.title}</div>
            <p className="text-slate-600">{section.description}</p>
          </div>

          <div className="space-y-6">
            {section.fields.map((field, index) => (
              <div key={field.name}>
                <Label htmlFor={field.name}>
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {field.type !== "textarea" ? (
                  <Input
                    id={field.name}
                    type={field.type}
                    ref={(el) => (inputRefs.current[index] = el)}
                    placeholder={field.placeholder}
                    value={formData[field.name] || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    className={errors[field.name] ? "border-red-500" : ""}
                  />
                ) : (
                  <Textarea
                    id={field.name}
                    rows={4}
                    ref={(el) => (inputRefs.current[index] = el)}
                    placeholder={field.placeholder}
                    value={formData[field.name] || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    className={errors[field.name] ? "border-red-500" : ""}
                  />
                )}
                {errors[field.name] && <p className="text-sm text-red-500 mt-1">{errors[field.name]}</p>}
              </div>
            ))}

            <Separator className="my-6" />

            <div className="flex flex-col sm:flex-row gap-3">
              {currentSection > 0 && (
                <Button type="button" variant="outline" onClick={handleBack}>
                  Previous
                </Button>
              )}
              <Button
                type="button"
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 border-0 text-white hover:bg-blue-700"
                onClick={handleNext}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...
                  </>
                ) : currentSection < totalSections - 1 ? (
                  "Continue"
                ) : (
                  "Submit KYC"
                )}
              </Button>
            </div>
          </div>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          🔒 Your information is encrypted using 256-bit SSL encryption.
        </p>
      </div>
    </div>
  );
}
